import React from 'react';
import { Typography } from '@mui/material';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import { sumBy } from 'lodash';
import EqualisationsAllocationsTable from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { Column as TableColumn } from 'further-ui/components/Table';

interface RealisationAllocation {
  holdingClassId: string;
  commitmentId: string;
  investmentId: string;
  investorLabel: string;
  allocationPercentage: number;
  originalAllocationPercentage: number;
  allocationType: AllocationRule;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  withholdingTaxExempt: boolean;
  unitsAllocated: number;
  excludeFromAllocation?: boolean;
}

interface RealisationAllocationsTableProps {
  realisationAllocations: Array<RealisationAllocation>;
  baseCurrency: CurrencyCode;
  allocationRule: AllocationRule;
}

const RealisationAllocationsTable: React.FC<
  RealisationAllocationsTableProps
> = ({ realisationAllocations, baseCurrency, allocationRule }) => {
  if (!realisationAllocations) {
    return <Typography variant="body2">No realisation allocations</Typography>;
  }

  const rows = realisationAllocations.map((allocation, idx) => ({
    id: idx.toString(),
    ...allocation,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investor',
      label: 'Investor',
      width: '15%',
      render: (row) => row.investorLabel,
      footer: `${
        realisationAllocations.filter((a) => !a.excludeFromAllocation).length
      } investors`,
    },
    {
      key: 'receivedConsiderationLcy',
      label: 'Received consideration',
      width: '14%',
      render: (row) =>
        numberToCurrencyString(row.receivedConsiderationLcy, {
          currency: baseCurrency,
        }),
      footer: numberToCurrencyString(
        sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'receivedConsiderationLcy',
        ),
        { currency: baseCurrency },
      ),
    },
    {
      key: 'earnoutConsiderationLcy',
      label: 'Earnout consideration',
      width: '14%',
      render: (row) =>
        numberToCurrencyString(row.earnoutConsiderationLcy, {
          currency: baseCurrency,
        }),
      footer: numberToCurrencyString(
        sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'earnoutConsiderationLcy',
        ),
        { currency: baseCurrency },
      ),
    },
    {
      key: 'escrowConsiderationLcy',
      label: 'Escrow consideration',
      width: '14%',
      render: (row) =>
        numberToCurrencyString(row.escrowConsiderationLcy, {
          currency: baseCurrency,
        }),
      footer: numberToCurrencyString(
        sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'escrowConsiderationLcy',
        ),
        { currency: baseCurrency },
      ),
    },
    {
      key: 'withholdingTaxPayableLcy',
      label: 'Withholding tax',
      width: '12%',
      render: (row) =>
        numberToCurrencyString(row.withholdingTaxPayableLcy, {
          currency: baseCurrency,
        }),
      footer: numberToCurrencyString(
        sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'withholdingTaxPayableLcy',
        ),
        { currency: baseCurrency },
      ),
    },
    {
      key: 'unitsAllocated',
      label: 'Units allocated',
      width: '12%',
      render: (row) => row.unitsAllocated.toFixed(2),
      footer: sumBy(
        realisationAllocations.filter((a) => !a.excludeFromAllocation),
        'unitsAllocated',
      ).toFixed(2),
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation (%)',
      width: '12%',
      render: (row) =>
        numberToPercentageString(row.allocationPercentage, {
          forceDecimals: true,
          accuracy: 2,
          noMultiply: true,
        }),
      footer: numberToPercentageString(
        sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'allocationPercentage',
        ),
        { forceDecimals: true, accuracy: 2, noMultiply: true },
      ),
    },
  ];

  const allocationRuleOptions = [
    { value: AllocationRule.Manual, label: 'Manual' },
    {
      value: AllocationRule.ByCommitmentAndClosingDate,
      label: 'By Commitment and Closing Date',
    },
    { value: AllocationRule.LinkedAction, label: 'Linked Action' },
    {
      value: AllocationRule.PerOriginalInvestment,
      label: 'Per Original Investment',
    },
  ];

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      showAllocationRule={true}
      allocationRule={allocationRule}
      allocationRuleOptions={allocationRuleOptions}
      isNotEditable={true}
    />
  );
};

export default RealisationAllocationsTable;
